/**
 * Fixed rentable-surface filter bounds (m²).
 * Typesense `area` is indexed as a float on building/project documents.
 */
export const LOCATION_AREA_MIN = 0;
export const LOCATION_AREA_MAX = 100_000;
export const LOCATION_AREA_STEP = 500;

/** Minimum-threshold presets for the search-bar dropdown (m²). */
export const LOCATION_AREA_PRESETS = [
	1000, 2000, 3000, 5000, 7500, 10000, 15000, 20000, 25000, 50000,
] as const;

export type LocationAreaPreset = (typeof LOCATION_AREA_PRESETS)[number];

export const isLocationAreaFilterActive = (min: number, max: number): boolean =>
	min > LOCATION_AREA_MIN || max < LOCATION_AREA_MAX;

export const clampLocationArea = (value: number): number => {
	const stepped = Math.round(value / LOCATION_AREA_STEP) * LOCATION_AREA_STEP;
	return Math.min(LOCATION_AREA_MAX, Math.max(LOCATION_AREA_MIN, stepped));
};

export const normalizeLocationAreaRange = (
	min: number,
	max: number,
): { min: number; max: number } => {
	const clampedMin = clampLocationArea(min);
	const clampedMax = clampLocationArea(max);

	return {
		min: Math.min(clampedMin, clampedMax),
		max: Math.max(clampedMin, clampedMax),
	};
};

export const formatLocationAreaPreset = (value: number): string =>
	`+ ${value.toLocaleString()} m²`;

/** Preset match: min is a preset value and max is at the upper bound. */
export const matchLocationAreaPreset = (min: number, max: number): number | null => {
	if (max < LOCATION_AREA_MAX || min <= LOCATION_AREA_MIN) {
		return null;
	}

	const preset = LOCATION_AREA_PRESETS.find((value) => value === min);
	return preset ?? null;
};

export const parseAreaValue = (value: unknown): number => {
	if (typeof value === "number" && !Number.isNaN(value)) {
		return value;
	}

	if (typeof value !== "string") {
		return 0;
	}

	const normalized = value
		.replace(/\s/g, "")
		.replace(/m\^?2/gi, "")
		.replace(/,/g, "");

	const parsed = parseFloat(normalized);
	return Number.isNaN(parsed) ? 0 : parsed;
};

/** Matches card display / Typesense resolver: entry area, else max(units, building parts). */
export const parseDocumentRentableArea = (
	document: {
		area?: unknown;
		units?: Array<{ area?: unknown; buildingPartTypes?: Array<{ area?: unknown }> }>;
		buildingPartTypes?: Array<{ area?: unknown }>;
	} | null | undefined,
): number => {
	if (!document) {
		return 0;
	}

	const entryArea = parseAreaValue(document.area);

	if (entryArea > 0) {
		return entryArea;
	}

	const unitsArea = (document.units ?? []).reduce((max, unit) => {
		const unitArea = parseAreaValue(unit.area);
		const fromParts = (unit.buildingPartTypes ?? []).reduce(
			(sum, part) => sum + parseAreaValue(part.area),
			0,
		);

		return Math.max(max, unitArea || fromParts);
	}, 0);

	const buildingPartsArea = (document.buildingPartTypes ?? []).reduce(
		(sum, part) => sum + parseAreaValue(part.area),
		0,
	);

	return Math.max(unitsArea, buildingPartsArea);
};

export const documentMatchesAreaFilter = (document: unknown, min: number, max: number): boolean => {
	if (!isLocationAreaFilterActive(min, max)) {
		return true;
	}

	const area = parseDocumentRentableArea(
		document as Parameters<typeof parseDocumentRentableArea>[0],
	);

	if (min > LOCATION_AREA_MIN && area < min) {
		return false;
	}

	if (max < LOCATION_AREA_MAX && area > max) {
		return false;
	}

	return true;
};

/** Typesense filter_by fragment for the area range (empty string when inactive). */
export const buildLocationAreaFilterQuery = (min: number, max: number): string => {
	if (!isLocationAreaFilterActive(min, max)) {
		return "";
	}

	const parts: string[] = [];

	if (min > LOCATION_AREA_MIN) {
		parts.push(`area:>=${min}`);
	}

	if (max < LOCATION_AREA_MAX) {
		parts.push(`area:<=${max}`);
	}

	return parts.length ? `${parts.join(" && ")} && ` : "";
};

export const formatLocationAreaRangeLabel = (min: number, max: number): string => {
	const preset = matchLocationAreaPreset(min, max);

	if (preset) {
		return formatLocationAreaPreset(preset);
	}

	if (min > LOCATION_AREA_MIN && max < LOCATION_AREA_MAX) {
		return `${min.toLocaleString()} – ${max.toLocaleString()} m²`;
	}

	if (min > LOCATION_AREA_MIN) {
		return `≥ ${min.toLocaleString()} m²`;
	}

	return `≤ ${max.toLocaleString()} m²`;
};
